import {
  ArrayControlProps,
  isControl,
  rankWith,
  resolveSchema,
} from "@jsonforms/core";
import { withJsonFormsArrayControlProps } from "@jsonforms/react";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Carousel, Icon, Loader } from "rsuite";
import { FileService } from "../../../services/FileService";
import { downloadBlob } from "../utils/fileDownload";
import { useAuthenticatedFileBlob } from "../utils/useAuthenticatedFileBlob";
import "./AttachmentPreviewRenderer.scss";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"];

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (date: Date): string => {
  return new Date(date).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const isImageFile = (nmExtension: string) =>
  IMAGE_EXTENSIONS.includes(nmExtension?.toLowerCase());

const downloadFile = (item: AttachmentItem): Promise<void> => {
  const url = FileService.getFileURL(item.idExternalFile, "original", "");
  return axios
    .get<Blob>(url, { responseType: "blob" })
    .then((res) => {
      downloadBlob(res.data, `${item.nmFile}.${item.nmExtension}`);
    });
};

interface AttachmentItem {
  nmFile: string;
  nmExtension: string;
  idExternalFile: string;
  nmUser: string;
  nrFileSize: number;
  dtIncluded: Date;
}

const AuthenticatedImage = ({
  idExternalFile,
  alt,
  onClick,
}: {
  idExternalFile: string;
  alt: string;
  onClick?: () => void;
}) => {
  const { blobUrl, isLoading } = useAuthenticatedFileBlob(idExternalFile);

  if (isLoading) {
    return <Loader size="md" />;
  }

  if (!blobUrl) {
    return (
      <div className="file-placeholder">
        <Icon icon="image" size="4x" />
        <span className="file-name">Preview unavailable</span>
      </div>
    );
  }

  return (
    <img
      src={blobUrl}
      alt={alt}
      className="preview-image"
      onClick={() => onClick?.()}
      style={{ cursor: "pointer" }}
    />
  );
};

const FullscreenPreview = ({
  items,
  initialIndex,
  onClose,
}: {
  items: AttachmentItem[];
  initialIndex: number;
  onClose: () => void;
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const currentItem = items[currentIndex];
  const isImage = isImageFile(currentItem.nmExtension);

  const { blobUrl, isLoading } = useAuthenticatedFileBlob(
    isImage ? currentItem.idExternalFile : undefined,
    "original",
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight")
        setCurrentIndex((prev) => (prev + 1) % items.length);
      if (e.key === "ArrowLeft")
        setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, items.length]);

  return createPortal(
    <div className="AttachmentPreviewRenderer">
      <div className="fullscreen-overlay" onClick={onClose}>
        <button
          className="fullscreen-nav fullscreen-nav-left"
          onClick={(e) => {
            e.stopPropagation();
            setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
          }}
        >
          <Icon icon="angle-left" size="3x" />
        </button>
        <div
          className="fullscreen-content"
          onClick={(e) => e.stopPropagation()}
        >
          {isImage && isLoading ? (
            <Loader size="lg" />
          ) : (
            <>
              {blobUrl ? (
                <img
                  src={blobUrl}
                  alt={currentItem.nmFile}
                  className="fullscreen-image"
                />
              ) : (
                <div className="fullscreen-no-preview">
                  <Icon icon="file" size="4x" />
                  <span>
                    {isImage
                      ? "This image could not be loaded"
                      : "Preview not available for this file type"}
                  </span>
                </div>
              )}
              <div className="fullscreen-caption">
                {currentItem.nmFile}.{currentItem.nmExtension} (
                {currentIndex + 1}/{items.length})
              </div>
            </>
          )}
        </div>
        <button
          className="fullscreen-nav fullscreen-nav-right"
          onClick={(e) => {
            e.stopPropagation();
            setCurrentIndex((prev) => (prev + 1) % items.length);
          }}
        >
          <Icon icon="angle-right" size="3x" />
        </button>
        <button className="fullscreen-close" onClick={onClose}>
          <Icon icon="close" size="2x" />
        </button>
      </div>
    </div>,
    document.body,
  );
};

const AuthenticatedThumbnail = ({
  idExternalFile,
  alt,
  onClick,
}: {
  idExternalFile: string;
  alt: string;
  onClick: () => void;
}) => {
  const { blobUrl, isLoading } = useAuthenticatedFileBlob(idExternalFile);

  return (
    <div className="thumbnail-item" onClick={onClick}>
      {isLoading && <Loader size="xs" />}
      {!isLoading && blobUrl && (
        <img src={blobUrl} alt={alt} className="thumbnail-image" />
      )}
      {!isLoading && !blobUrl && (
        <div className="thumbnail-placeholder">
          <Icon icon="image" size="lg" />
        </div>
      )}
    </div>
  );
};

const DownloadButton = ({ item }: { item: AttachmentItem }) => {
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");

  const handleClick = () => {
    setState("loading");
    downloadFile(item)
      .then(() => setState("idle"))
      .catch(() => setState("error"));
  };

  return (
    <>
      <button
        className="download-button"
        onClick={handleClick}
        disabled={state === "loading"}
      >
        <Icon icon={state === "loading" ? "spinner" : "download"} spin={state === "loading"} />{" "}
        {state === "loading" ? "Downloading..." : "Download"}
      </button>
      {state === "error" && (
        <span className="download-error">Download failed. Please try again.</span>
      )}
    </>
  );
};

const AttachmentPreviewRenderer = (props: ArrayControlProps) => {
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const goToSlide = (index: number) => {
    const inputs = carouselRef.current?.querySelectorAll<HTMLInputElement>(
      ".rs-carousel-label-wrapper input[type='radio']",
    );
    inputs?.[index]?.click();
  };

  if (!Array.isArray(props.data) || props.data.length === 0) {
    return (
      <div className="group-container">
        <div className="no-data">Nothing to display</div>
      </div>
    );
  }

  return (
    <div
      className="group-container AttachmentPreviewRenderer"
      ref={carouselRef}
    >
      {fullscreenIndex !== null && (
        <FullscreenPreview
          items={props.data}
          initialIndex={fullscreenIndex}
          onClose={() => setFullscreenIndex(null)}
        />
      )}
      <Carousel autoplay placement="bottom" shape="dot">
        {props.data.map((item: AttachmentItem, index: number) => {
          const isImage = isImageFile(item.nmExtension);

          return (
            <div key={item.idExternalFile ?? index} className="carousel-slide">
              <div className="slide-preview">
                {isImage ? (
                  <AuthenticatedImage
                    idExternalFile={item.idExternalFile}
                    alt={item.nmFile}
                    onClick={() => setFullscreenIndex(index)}
                  />
                ) : (
                  <div className="file-placeholder">
                    <Icon icon="file" size="4x" />
                  </div>
                )}
              </div>
              <div className="slide-details">
                <div className="detail-row">
                  <span className="detail-label">File Name</span>
                  <span className="detail-value">
                    {item.nmFile}.{item.nmExtension}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Size</span>
                  <span className="detail-value">
                    {formatBytes(item.nrFileSize)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Uploaded By</span>
                  <span className="detail-value">{item.nmUser}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date</span>
                  <span className="detail-value">
                    {formatDate(item.dtIncluded)}
                  </span>
                </div>
                <DownloadButton item={item} />
              </div>
            </div>
          );
        })}
      </Carousel>
      <div className="thumbnail-strip">
        {props.data.map((item: AttachmentItem, index: number) => {
          const isImage = isImageFile(item.nmExtension);

          return isImage ? (
            <AuthenticatedThumbnail
              key={item.idExternalFile ?? index}
              idExternalFile={item.idExternalFile}
              alt={item.nmFile}
              onClick={() => goToSlide(index)}
            />
          ) : (
            <div
              key={item.idExternalFile ?? index}
              className="thumbnail-item"
              onClick={() => goToSlide(index)}
            >
              <div className="thumbnail-placeholder">
                <Icon icon="file" size="lg" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const attachmentPreviewTester = rankWith(
  100,
  (uischema, schema, context) => {
    if (!isControl(uischema)) return false;
    if (uischema.options?.variant !== "attachmentPreview") return false;
    const resolvedSchema = resolveSchema(
      schema,
      uischema.scope,
      context?.rootSchema,
    );
    return resolvedSchema?.type === "array";
  },
);

export default withJsonFormsArrayControlProps(AttachmentPreviewRenderer);
